/**
 * Admin → Desktop app: the desktop uploader build every tenant is offered.
 *
 * Platform-wide rather than per-tenant, which is why it sits in the ADMIN group
 * next to System admin instead of under Settings. Publishing a build is this
 * form being saved — the installer URLs are data, so a new version needs no
 * redeploy.
 *
 * The preview on the right is not decoration: a system_admin has no tenant, so
 * it cannot open the Settings → Desktop uploader screen its own publish decides the
 * contents of. Without the preview it would publish blind and hear about
 * mistakes from a customer.
 */
import { useCallback, useEffect, useState } from 'react';

import type { DesktopReleaseView } from '../../api/schema';
import { Button, ErrorState, FormField, Input, Skeleton, Toggle, useToast } from '../../components';
import { useLoadState } from '../../lib/useLoadState';
import { MacosIcon, WindowsIcon } from '../../shell/icons';

import { useAdminApi } from './api';
import { fmtTimestamp } from './format';
import {
  canPublish,
  everyUrlAcceptable,
  isAcceptableUrl,
  isDirty,
  sizeLabel,
  toForm,
  toPayload,
  type BuildForm,
  type ReleaseForm,
} from './desktopRelease';
import './admin.css';

type Platform = 'windows' | 'macos';

const PLATFORM_LABEL: Record<Platform, string> = { windows: 'Windows', macos: 'macOS' };
const PLATFORM_EXTENSION: Record<Platform, string> = { windows: '.exe', macos: '.dmg' };

const HTTPS_HINT = 'Must be a full https:// address.';

// -------------------------------------------------------------- build fields --

/**
 * One platform's fields. Clearing the installer URL is how an admin withdraws a
 * platform — the server drops the whole build, so a stale size cannot reappear
 * with the next URL. The hint says so rather than leaving it to be discovered.
 */
function BuildCard({
  platform,
  value,
  onChange,
}: {
  platform: Platform;
  value: BuildForm;
  onChange: (next: BuildForm) => void;
}): React.JSX.Element {
  const set = (patch: Partial<BuildForm>): void => {
    onChange({ ...value, ...patch });
  };
  const isWindows = platform === 'windows';
  const urlError = isAcceptableUrl(value.url) ? undefined : HTTPS_HINT;
  const altError = isAcceptableUrl(value.altUrl) ? undefined : HTTPS_HINT;

  return (
    <section className="adm-card" aria-label={`${PLATFORM_LABEL[platform]} build`}>
      <header className="adm-card__head">
        <div>
          <h2 className="adm-card__title adm-desktop__row">
            {platform === 'windows' ? <WindowsIcon /> : <MacosIcon />}
            {PLATFORM_LABEL[platform]} build
          </h2>
          <p className="adm-card__sub">
            Optional. Leave the installer URL empty and customers are offered{' '}
            {isWindows ? 'macOS' : 'Windows'} only.
          </p>
        </div>
      </header>

      <FormField label={`Installer URL (${PLATFORM_EXTENSION[platform]})`} error={urlError}>
        {(p) => (
          <Input
            {...p}
            className="adm-mono"
            value={value.url}
            placeholder="https://…"
            autoComplete="off"
            onChange={(event) => {
              set({ url: event.currentTarget.value });
            }}
          />
        )}
      </FormField>

      <div className="adm-desktop__pair">
        <FormField label="File size (MB)">
          {(p) => (
            <Input
              {...p}
              type="number"
              min={0}
              step={0.1}
              value={value.sizeMb}
              onChange={(event) => {
                set({ sizeMb: event.currentTarget.value });
              }}
            />
          )}
        </FormField>
        <FormField label="Minimum OS">
          {(p) => (
            <Input
              {...p}
              value={value.minOs}
              placeholder={isWindows ? 'Windows 10 or later' : 'macOS 12 or later'}
              autoComplete="off"
              onChange={(event) => {
                set({ minOs: event.currentTarget.value });
              }}
            />
          )}
        </FormField>
      </div>

      {/* Windows only: there is no macOS equivalent of an MSI an IT team pushes. */}
      {isWindows ? (
        <FormField label="MSI for IT deployment (optional)" error={altError}>
          {(p) => (
            <Input
              {...p}
              className="adm-mono"
              value={value.altUrl}
              placeholder="https://…"
              autoComplete="off"
              onChange={(event) => {
                set({ altUrl: event.currentTarget.value });
              }}
            />
          )}
        </FormField>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------------------- preview --

function PreviewCard({
  platform,
  build,
  version,
}: {
  platform: Platform;
  build: BuildForm;
  version: string;
}): React.JSX.Element {
  const meta = [build.minOs.trim(), sizeLabel(build.sizeMb)].filter(Boolean).join(' · ');
  return (
    <div className="adm-desktop__pcard">
      <span className="adm-desktop__pglyph">
        {platform === 'windows' ? <WindowsIcon /> : <MacosIcon />}
      </span>
      <span className="adm-desktop__pname">{PLATFORM_LABEL[platform]}</span>
      {meta === '' ? null : <span className="adm-desktop__pmeta">{meta}</span>}
      <span className="adm-desktop__pbtn">Download {PLATFORM_EXTENSION[platform]}</span>
      <span className="adm-desktop__pmeta">
        {version === '' ? '—' : `v${version}`}
        {build.altUrl.trim() === '' ? '' : ' · MSI'}
      </span>
    </div>
  );
}

/** What a tenant admin would see on Settings → Desktop uploader. */
function ReleasePreview({ form }: { form: ReleaseForm }): React.JSX.Element {
  const hasWindows = form.windows.url.trim() !== '';
  const hasMac = form.macos.url.trim() !== '';
  const version = form.version.trim();

  return (
    <div className="adm-card">
      <p className="adm-eyebrow">What customers see</p>
      <p className="adm-card__title">Docsight Uploader</p>
      <p className="adm-card__sub">
        A small desktop app for people who need to send documents in regularly.
      </p>
      {!form.published ? (
        <p className="adm-desktop__pempty">Not published — customers see no download at all.</p>
      ) : !hasWindows && !hasMac ? (
        <p className="adm-desktop__pempty">
          Downloads are temporarily unavailable. Please try again shortly.
        </p>
      ) : (
        <div className={hasWindows && hasMac ? 'adm-desktop__pgrid' : undefined}>
          {hasWindows ? (
            <PreviewCard platform="windows" build={form.windows} version={version} />
          ) : null}
          {hasMac ? <PreviewCard platform="macos" build={form.macos} version={version} /> : null}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------- screen --

/** Why the release cannot go live, or `null` when it can. */
function blockedReason(form: ReleaseForm): string | null {
  if (canPublish(form)) return null;
  if (form.version.trim() === '') return 'Add a version before publishing.';
  if (form.windows.url.trim() === '' && form.macos.url.trim() === '') {
    return 'Add an installer URL for Windows or macOS — either one on its own is enough.';
  }
  return 'Fix the highlighted links before publishing.';
}

/** What publishing this form would actually do, in plain words. */
function outcome(form: ReleaseForm): string {
  const win = form.windows.url.trim() !== '';
  const mac = form.macos.url.trim() !== '';
  if (win && mac) return 'Both platforms have an installer — customers see two download cards.';
  if (win) return 'Windows only — customers see a single Windows card. Add a macOS URL to offer both.';
  if (mac) return 'macOS only — customers see a single macOS card. Add a Windows URL to offer both.';
  return 'Either platform on its own is enough: fill in Windows, macOS, or both.';
}

function DesktopAppEditor({
  release,
  onSaved,
}: {
  release: DesktopReleaseView;
  onSaved: (saved: DesktopReleaseView) => void;
}): React.JSX.Element {
  const api = useAdminApi();
  const { toast } = useToast();

  // Seeded once from the record this component was mounted with, and re-seeded
  // only by a save. A refetch landing mid-edit must never overwrite half-typed
  // installer URLs — copying one in from the build pipeline means leaving the
  // tab and coming back.
  const [form, setForm] = useState<ReleaseForm>(() => toForm(release));
  const [saved, setSaved] = useState<ReleaseForm>(() => toForm(release));
  const [saving, setSaving] = useState(false);

  const publishable = canPublish(form);
  const urlsValid = everyUrlAcceptable(form);
  const dirty = isDirty(form, saved);
  const blocked = blockedReason(form);

  /*
   * A live release whose record is no longer publishable. Publishing state is
   * the admin's decision, so the screen does not silently flip the switch off:
   * it says what is wrong and blocks the save until they resolve it — otherwise
   * finishing a valid edit could take a live release dark with no warning.
   */
  const publishedButBroken = form.published && !publishable;

  const save = useCallback(() => {
    setSaving(true);
    void (async () => {
      try {
        const result = await api.desktopRelease.save(toPayload(form));
        const next = toForm(result);
        setForm(next);
        setSaved(next);
        onSaved(result);
        toast('Desktop release saved');
      } catch {
        toast('Could not save the release — try again', { tone: 'error' });
      } finally {
        setSaving(false);
      }
    })();
  }, [api, form, onSaved, toast]);

  const live = release.published_at !== null && release.published_at !== undefined;
  const stamp = release.published_at ?? release.updated_at ?? null;
  const stampLabel =
    stamp === null ? 'Not saved yet' : `${live ? 'Published' : 'Last saved'} ${fmtTimestamp(stamp)}`;

  return (
    <div className="adm-desktop">
      <div className="adm-desktop__main">
        <section className="adm-card" aria-label="Visible to customers">
          <header className="adm-card__head">
            <div>
              <h2 className="adm-card__title adm-desktop__row">
                Visible to customers
                <span
                  className={`adm-partner-status adm-partner-status--${form.published ? 'ok' : 'bad'} adm-desktop__pill`}
                >
                  <span
                    className={`adm-dot adm-dot--${form.published ? 'ok' : 'bad'}`}
                    aria-hidden="true"
                  />
                  {form.published ? 'Published' : 'Not published'}
                </span>
              </h2>
              <p className="adm-card__sub">
                {form.published
                  ? 'Every organisation gets a Settings → Desktop uploader tab with this build on it.'
                  : 'While this is off, no customer sees the Desktop uploader tab at all.'}
              </p>
            </div>
            <Toggle
              checked={form.published}
              // Turning it OFF must always be possible: if a live release has
              // become unpublishable, a disabled switch would leave the admin no
              // way out of the very state the warning asks them to resolve.
              disabled={!publishable && !form.published}
              label="Visible to customers"
              onChange={(checked) => {
                setForm({ ...form, published: checked });
              }}
            />
          </header>
          <p
            className={blocked === null ? 'adm-note' : 'adm-docfields-warn'}
            role={blocked === null ? undefined : 'alert'}
          >
            {publishedButBroken
              ? `This release is live but no longer complete: ${blocked ?? ''} Fix it, or switch off "Visible to customers", before saving.`
              : (blocked ?? outcome(form))}
          </p>
        </section>

        <section className="adm-card" aria-label="Release">
          <header className="adm-card__head">
            <div>
              <h2 className="adm-card__title">Release</h2>
              <p className="adm-card__sub">Shown under each download button.</p>
            </div>
          </header>
          <div className="adm-desktop__pair">
            <FormField label="Version">
              {(p) => (
                <Input
                  {...p}
                  className="adm-mono"
                  value={form.version}
                  placeholder="1.0.0"
                  maxLength={40}
                  autoComplete="off"
                  onChange={(event) => {
                    setForm({ ...form, version: event.currentTarget.value });
                  }}
                />
              )}
            </FormField>
            <FormField
              label="Release notes URL (optional)"
              error={isAcceptableUrl(form.notesUrl) ? undefined : HTTPS_HINT}
            >
              {(p) => (
                <Input
                  {...p}
                  className="adm-mono"
                  value={form.notesUrl}
                  placeholder="https://…"
                  autoComplete="off"
                  onChange={(event) => {
                    setForm({ ...form, notesUrl: event.currentTarget.value });
                  }}
                />
              )}
            </FormField>
          </div>
        </section>

        <BuildCard
          platform="windows"
          value={form.windows}
          onChange={(windows) => {
            setForm({ ...form, windows });
          }}
        />
        <BuildCard
          platform="macos"
          value={form.macos}
          onChange={(macos) => {
            setForm({ ...form, macos });
          }}
        />

        <div className="adm-card adm-card--pad adm-desktop__savebar">
          <span className="adm-muted">
            {dirty ? 'Unsaved changes' : stampLabel}
            {release.updated_by === null || release.updated_by === undefined
              ? ''
              : ` · ${release.updated_by}`}
          </span>
          <div className="adm-row-actions">
            <Button
              variant="secondary"
              disabled={!dirty || saving}
              onClick={() => {
                setForm(saved);
              }}
            >
              Discard changes
            </Button>
            <Button
              variant="primary"
              loading={saving}
              // publishedButBroken blocks the save that would otherwise take a
              // live release dark without the admin ever choosing to.
              disabled={!dirty || !urlsValid || publishedButBroken}
              onClick={save}
            >
              Save
            </Button>
          </div>
        </div>
      </div>

      <aside className="adm-desktop__aside">
        <ReleasePreview form={form} />
        <p className="adm-footnote">
          A system admin has no Settings screen of their own, so this preview is the only way to see
          what was published.
        </p>
      </aside>
    </div>
  );
}

export function DesktopAppScreen(): React.JSX.Element {
  const api = useAdminApi();
  const fetchRelease = useCallback(
    (signal: AbortSignal): Promise<DesktopReleaseView> => api.desktopRelease.get(signal),
    [api],
  );
  const state = useLoadState(fetchRelease);

  // The editor is seeded from the record and keyed on it, so a save remounts it
  // with the stored truth while an unrelated rerender leaves the draft alone.
  const [seed, setSeed] = useState<DesktopReleaseView | null>(null);
  const loaded = state.status === 'ready' ? state.data : null;
  useEffect(() => {
    if (loaded !== null) setSeed(loaded);
  }, [loaded]);

  if (state.status === 'loading') {
    return (
      <div className="adm-screen" aria-busy="true">
        <Skeleton height={420} />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="adm-screen">
        <ErrorState
          title="Couldn't load the desktop release"
          body="The platform config service didn't respond."
          traceId={state.traceId ?? undefined}
          actions={
            <Button variant="secondary" onClick={state.reload}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (seed === null) return <div className="adm-screen" />;

  return (
    <div className="adm-screen">
      <DesktopAppEditor
        key={seed.updated_at ?? 'new'}
        release={seed}
        onSaved={(result) => {
          setSeed(result);
        }}
      />
    </div>
  );
}
